import { z } from "zod";
import { PublicIdSchema } from "./common";

/**
 * 請求書明細の行モード (F25 × F29 連携)。
 * - 'aggregated': カテゴリ集計 (注文一覧計 / 各特殊運賃計 / その他)
 * - 'detailed': 配送1件=1明細
 */
export const InvoiceLineMode = z.enum(["aggregated", "detailed"]);

/** 締め日・支払日: 1〜31 (日付) または 99 (月末)。 */
const DayOfMonthOrEom = z
  .number()
  .int()
  .refine((v) => (v >= 1 && v <= 31) || v === 99, "1〜31 または 99 (月末) を指定してください");

/**
 * 支払サイト (payment_month_offset / payment_day) は対でのみ意味を持つ。
 * 片方だけの設定・片方だけのクリアを弾く (undefined は「未指定」として無視)。
 */
function refinePaymentTerms(
  input: { payment_month_offset?: number | null; payment_day?: number | null },
  ctx: z.RefinementCtx,
): void {
  const offset = input.payment_month_offset ?? null;
  const day = input.payment_day ?? null;
  if ((offset === null) !== (day === null)) {
    ctx.addIssue({
      code: "custom",
      message: "支払サイトは支払月と支払日を対で設定してください",
      path: [offset === null ? "payment_month_offset" : "payment_day"],
    });
  }
}

export const Client = z.object({
  id: PublicIdSchema,
  client_code: z.string().nullable(),
  name: z.string(),
  phone: z.string().nullable(),
  remarks: z.string().nullable(),
  /**
   * 集荷 (出荷元) 住所。出荷元が固定の取引先向けの既定値 (Issue #613)。
   * 注文書に集荷住所の記載がないときの補完元で、注文書に記載があればそちらが正。
   */
  pickup_address: z.string().nullable(),
  /** 集荷 (出荷元) 住所の緯度・経度。補完時に受注へ複製する (未登録は null)。 */
  pickup_latitude: z.string().nullable(),
  pickup_longitude: z.string().nullable(),
  invoice_line_mode: InvoiceLineMode,
  /** 請求宛名。null の場合は name を宛名に使用する (F25)。 */
  billing_name: z.string().nullable(),
  billing_postal_code: z.string().nullable(),
  billing_address: z.string().nullable(),
  billing_department: z.string().nullable(),
  billing_contact_name: z.string().nullable(),
  /** 請求書送付先メール。保存のみ (送付機能は未実装)。 */
  billing_email: z.string().nullable(),
  /** 締め日 (1-31, 99=月末)。集計期間には影響しない (表示・将来拡張用)。 */
  closing_day: z.number().int().nullable(),
  /** 支払月オフセット (0=当月, 1=翌月, …)。payment_day と対で支払期日を自動算出する。 */
  payment_month_offset: z.number().int().nullable(),
  /** 支払日 (1-31, 99=月末)。 */
  payment_day: z.number().int().nullable(),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
});

/** 集荷 (出荷元) 座標。DB は real だが、他マスタと同じく文字列で受け渡す。 */
const PickupCoordinateFields = {
  pickup_latitude: z.string().max(32).nullable().optional(),
  pickup_longitude: z.string().max(32).nullable().optional(),
} as const;

function refineCoordinateValue(
  raw: string,
  range: { min: number; max: number },
  path: "pickup_latitude" | "pickup_longitude",
  message: string,
  ctx: z.RefinementCtx,
): void {
  const value = raw.trim() === "" ? Number.NaN : Number(raw);
  if (!Number.isFinite(value) || value < range.min || value > range.max) {
    ctx.addIssue({ code: "custom", message, path: [path] });
  }
}

/**
 * 集荷座標 (pickup_latitude / pickup_longitude) は対でのみ意味を持つ。
 * 片方だけの設定・片方だけのクリアと、数値として解釈できない値を弾く
 * (undefined は「未指定」として無視)。
 */
function refinePickupCoordinates(
  input: { pickup_latitude?: string | null; pickup_longitude?: string | null },
  ctx: z.RefinementCtx,
): void {
  const latitude = input.pickup_latitude ?? null;
  const longitude = input.pickup_longitude ?? null;
  if ((latitude === null) !== (longitude === null)) {
    ctx.addIssue({
      code: "custom",
      message: "集荷座標は緯度と経度を対で設定してください",
      path: [latitude === null ? "pickup_latitude" : "pickup_longitude"],
    });
    return;
  }
  if (latitude === null || longitude === null) return;
  refineCoordinateValue(
    latitude,
    { min: -90, max: 90 },
    "pickup_latitude",
    "緯度は -90〜90 の数値で入力してください",
    ctx,
  );
  refineCoordinateValue(
    longitude,
    { min: -180, max: 180 },
    "pickup_longitude",
    "経度は -180〜180 の数値で入力してください",
    ctx,
  );
}

export const ListClientsInput = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().optional(),
  /** 名称・取引先コードの部分一致 */
  q: z.string().trim().max(200).optional(),
  // false で総件数 COUNT を省略 (コンボボックス等 total 不要の呼出向け)。
  // このとき pagination.total は「取得件数 (続きがあれば limit+1)」を返す。
  // z.coerce.boolean() は "false" を true 扱いするため preprocess で解釈する。
  with_total: z
    .preprocess((v) => (typeof v === "string" ? v === "true" : v), z.boolean())
    .default(true),
});

const BillingFields = {
  billing_name: z.string().max(200).nullable().optional(),
  billing_postal_code: z.string().max(20).nullable().optional(),
  billing_address: z.string().max(500).nullable().optional(),
  billing_department: z.string().max(100).nullable().optional(),
  billing_contact_name: z.string().max(100).nullable().optional(),
  billing_email: z.email("メールアドレスの形式で入力してください").nullable().optional(),
  closing_day: DayOfMonthOrEom.nullable().optional(),
  payment_month_offset: z.number().int().min(0).max(6).nullable().optional(),
  payment_day: DayOfMonthOrEom.nullable().optional(),
} as const;

export const CreateClientInput = z
  .object({
    client_code: z.string().max(50).nullable().optional(),
    name: z.string().min(1).max(200),
    phone: z.string().max(50).nullable().optional(),
    remarks: z.string().nullable().optional(),
    pickup_address: z.string().max(500).nullable().optional(),
    ...PickupCoordinateFields,
    invoice_line_mode: InvoiceLineMode.optional(),
    ...BillingFields,
  })
  .superRefine(refinePaymentTerms)
  .superRefine(refinePickupCoordinates);

export const UpdateClientInput = z
  .object({
    client_code: z.string().max(50).nullable().optional(),
    name: z.string().min(1).max(200),
    phone: z.string().max(50).nullable().optional(),
    remarks: z.string().nullable().optional(),
    pickup_address: z.string().max(500).nullable().optional(),
    ...PickupCoordinateFields,
    invoice_line_mode: InvoiceLineMode.optional(),
    ...BillingFields,
  })
  .superRefine(refinePaymentTerms)
  .superRefine(refinePickupCoordinates);

export type ClientDto = z.infer<typeof Client>;
export type CreateClientInputDto = z.infer<typeof CreateClientInput>;
export type UpdateClientInputDto = z.infer<typeof UpdateClientInput>;
export type InvoiceLineModeDto = z.infer<typeof InvoiceLineMode>;
